<template>
  <NodeViewWrapper
    class="slide-deck-container"
    data-type="slide-deck"
    :data-block-id="props.node.attrs.blockId"
    :class="{ selected: props.selected }"
  >
    <div class="slide-deck-content">
      <div class="slide-deck-thumbnail">
        <img
          v-if="props.node.attrs.thumbnailUrl"
          :src="props.node.attrs.thumbnailUrl"
          :alt="`Slide ${props.node.attrs.slideIndex + 1}`"
        />
        <div class="slide-deck-title">
          {{
            props.node.attrs.title || `Slide ${props.node.attrs.slideIndex + 1}`
          }}
        </div>
      </div>
      <div class="slide-deck-info">
        <NodeViewContent class="slide-deck-notes" />
      </div>
    </div>
  </NodeViewWrapper>
</template>

<script setup>
import { NodeViewContent, NodeViewWrapper } from "@tiptap/vue-3";
import { defineProps } from "vue";

const props = defineProps({
  editor: {
    type: Object,
    required: true,
  },
  node: {
    type: Object,
    required: true,
  },
  updateAttributes: {
    type: Function,
    required: true,
  },
  selected: {
    type: Boolean,
    default: false,
  },
});
</script>

<style>
.slide-deck-container {
  margin: 1.5rem 0;
  border-radius: 8px;
  background-color: var(--white-hover-1);
  position: relative;
}

.slide-deck-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #0080ff;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.85rem;
}

.slide-deck-link:hover {
  text-decoration: underline;
}

.slide-deck-container.selected {
  outline: 2px solid #0080ff;
  box-shadow: 0 0 0 2px rgba(0, 128, 255, 0.2);
}

.slide-deck-content {
  display: flex;
  flex-direction: row;
  gap: 16px;
  padding: 8px;
  align-items: flex-start;
}

.slide-deck-thumbnail {
  flex-shrink: 0;
  width: 469px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  cursor: pointer;
  transition: all 0.2s ease;
}

.slide-deck-thumbnail:hover {
  opacity: 0.9;
}

.slide-deck-thumbnail:hover img {
  transform: scale(1.03);
}

.slide-deck-thumbnail img {
  width: 100%;
  height: 265px;
  object-fit: cover;
  border-radius: 0.75rem;
  transition: transform 0.2s ease;
}

.slide-deck-title {
  font-weight: 500;
  color: var(--black-body-text);
  margin-top: 0.5rem;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.08px;
}

.slide-deck-info {
  flex: 1 0 0;
  min-width: 1px;
  padding-left: 16px;
  border-left: 2px solid var(--neutral-100);
  align-self: center;
}

.slide-deck-notes {
  color: var(--black-body-text);
  font-size: var(--fs-big);
  font-weight: 400;
  line-height: 28px;
  letter-spacing: 0.15px;
  font-family: "League Spartan", sans-serif;
  min-height: 50px;
}

/* Style for the ProseMirror content inside slide notes */
.slide-deck-notes :deep(.ProseMirror) {
  outline: none;
  min-height: 30px;
}

.slide-deck-notes :deep(.ProseMirror p.is-editor-empty:first-child::before) {
  content: "Type your notes here...";
  float: left;
  color: #adb5bd;
  pointer-events: none;
  height: 0;
}

.slide-notes-label {
  font-weight: 500;
  color: #343a40;
  margin-bottom: 5px;
  font-size: 0.9rem;
}

.node-controls {
  position: absolute;
  top: 5px;
  right: 5px;
  display: flex;
  gap: 5px;
}

.node-control-btn {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #dee2e6;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.node-control-btn:hover {
  background: #f8f9fa;
  color: #dc3545;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .slide-deck-content {
    flex-direction: column;
  }

  .slide-deck-thumbnail {
    width: 100%;
  }

  .slide-deck-thumbnail img {
    height: auto;
    aspect-ratio: 469 / 265;
  }

  .slide-deck-info {
    border-left: none;
    padding-left: 0;
  }

  .slide-deck-notes {
    font-size: var(--fs-small);
    line-height: 20px;
    text-align: left;
  }
}
</style>
